import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import usePageTitle from '../hooks/usePageTitle';
import { getLoungePost } from '../api';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

const LoungePost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  const loadPost = useCallback(() => {
    setPost(null); setError(false);
    getLoungePost(slug).then(setPost).catch(() => setError(true));
  }, [slug, setPost, setError]);

  useEffect(() => { loadPost(); }, [loadPost]);

  const paragraphs = useMemo(
    () => (post?.content || '').split('\n').filter((p) => p.trim()),
    [post]
  );

  usePageTitle(post ? post.title : 'The Lounge');

  if (error) {
    return (
      <main className="pt-40 pb-32 min-h-screen bg-[#FBFAF8]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <h1 className="font-display text-4xl text-[#241033] mb-4">Reflection not found</h1>
          <Link to="/lounge" className="text-[#43265E] tracking-luxe uppercase text-sm">Back to The Lounge</Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="pt-40 pb-32 min-h-screen bg-[#FBFAF8]">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="animate-pulse space-y-6">
            <div className="h-6 w-40 bg-[#EDE8F2] rounded" />
            <div className="h-12 w-3/4 bg-[#EDE8F2] rounded" />
            <div className="h-80 w-full bg-[#EDE8F2] rounded" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#FBFAF8]">
      <article className="pt-36 pb-24">
        <div className="max-w-[820px] mx-auto px-6">
          <Link to="/lounge" className="inline-flex items-center gap-2 text-[#7E5C9E] hover:text-[#43265E] text-sm tracking-luxe uppercase mb-10 transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> The Lounge
          </Link>
          <Reveal>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="eyebrow text-[#7E5C9E]">{post.category}</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#6A6570] tracking-luxe uppercase"><Clock className="w-3 h-3" strokeWidth={1.5} /> {post.readTime}</span>
              {post.date && <span className="inline-flex items-center gap-1.5 text-xs text-[#6A6570] tracking-luxe uppercase"><Calendar className="w-3 h-3" strokeWidth={1.5} /> {post.date}</span>}
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-10">{post.title}</h1>
          </Reveal>
          {post.image && (
            <Reveal delay={100}>
              <img src={post.image} alt={post.title} className="w-full h-[420px] object-cover rounded-sm mb-12" />
            </Reveal>
          )}
          <Reveal delay={150}>
            <div className="prose-luxe">
              {paragraphs.map((para, i) => (
                <p key={`${i}-${para.slice(0, 24)}`} className="text-[#3A3444] font-light text-lg leading-[1.9] mb-6">{para}</p>
              ))}
            </div>
          </Reveal>
          <div className="hairline my-14" />
          <p className="font-display italic text-2xl text-[#43265E]">TAG us in.</p>
        </div>
      </article>
    </main>
  );
};

export default LoungePost;
